/*******************************************************************************
 * Copyright (c) 2024 Passport Fintech Inc. All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 ******************************************************************************/

import {
  PspToken,
  PspTokenApiResponse,
  pspTokenFromApiResponse,
} from '@shared/models/psp-token';
import {VerifyType} from '@shared/modules/auth/models/verify-type';

export interface LoginDetails {
  userId: string;
  accessToken: PspToken;
  hasMfaSetup: boolean;
  needsNda: boolean;
  ndaSigned: boolean;
  isActive: boolean;
  mfaType?: VerifyType;
}

/**
 * This interface describes how the LoginApiResponse is returned by the API,
 * so we can leverage the strict typing capabilities and not rely on "any"
 */
export interface LoginApiResponse {
  user_id: string;
  access_token: PspTokenApiResponse;
  has_mfa_setup: boolean;
  needs_nda: boolean;
  nda_signed: boolean;
  is_active: boolean;
  mfa_type?: VerifyType;
  base_user_id?: string;
}

/**
 * This method transforms the LoginApiResponse into our own
 * LoginDetails object which our application understands
 * @param response the pagination API response
 * @returns the deserialized LoginDetails
 */
export const loginDetailsFromResponse = (response: LoginApiResponse): LoginDetails => {
  return {
    userId: response.base_user_id ? response.base_user_id : response.user_id,
    accessToken: pspTokenFromApiResponse(response.access_token),
    hasMfaSetup: response.has_mfa_setup,
    needsNda: false,
    ndaSigned: response.nda_signed,
    isActive: response.is_active,
    ...(response.mfa_type && {mfaType: response.mfa_type}),
  };
};

export interface AdminLoginDetails {
  userId: string;
  accessToken: PspToken;
  hasMfaSetup: boolean;
}

export interface AdminLoginApiResponse {
  user_id: string;
  access_token: PspTokenApiResponse;
  has_mfa_setup: boolean;
}

/**
 * This method transforms the AdminLoginApiResponse into our own
 * AdminLoginDetails object which our application understands
 * @param response the pagination API response
 * @returns the deserialized LoginDetails
 */
export const adminLoginDetailsFromResponse = (
  response: AdminLoginApiResponse
): AdminLoginDetails => {
  return {
    userId: response.user_id,
    accessToken: pspTokenFromApiResponse(response.access_token),
    hasMfaSetup: response.has_mfa_setup,
  };
};
